/**
 * BLRRD Editor — Pointer-based pinch-zoom + 2-finger pan.
 *
 * Wires onto the canvas-area element using Pointer Events (works for touch,
 * pen, and mouse uniformly). Single-pointer interactions are NEVER claimed —
 * we yield to brush, eyedropper, crop, and other tools that own a single
 * pointer. Two-pointer gestures get exclusive treatment via setPointerCapture.
 *
 * Math: pinch keeps the gesture's focal point pinned in screen space. The
 * canvas-wrap uses `transform-origin: center center` and a transform string
 * `translate(panX,panY) scale(zoom)`, so the inversion looks like:
 *
 *     focal_canvas = (focal_screen - origin - pan) / zoom         (pre)
 *     pan'         = focal_screen - origin - zoom' * focal_canvas (post)
 *
 * Per the Phase 1.E plan: `touch-action: none` is set in CSS on the canvas
 * area so the browser doesn't fight us with native scroll/double-tap-zoom.
 */

const ZOOM_MIN = 0.05;
const ZOOM_MAX = 15;
const PINCH_THRESHOLD_PX = 6;   /* don't start pinching from noise */

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

function dist(p1, p2) {
    const dx = p1.x - p2.x, dy = p1.y - p2.y;
    return Math.hypot(dx, dy);
}

function midpoint(p1, p2) {
    return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
}

/**
 * Attach pinch-zoom + 2-finger pan to a canvas area.
 *
 * @param {HTMLElement} area     The element receiving pointer events
 *                               (has the bounding box used for origin math).
 * @param {object} state         Mutable state object with { zoom, panX, panY }.
 * @param {Function} onUpdate    Called after each gesture frame to apply the
 *                               new transform + redraw. Receives `state`.
 * @returns {Function} detach    Removes all listeners.
 */
export function attachCanvasGestures(area, state, onUpdate) {
    if (!area || !state || typeof onUpdate !== 'function') return () => {};

    const pointers = new Map();          /* pointerId → { x, y } */
    let gesture = null;                  /* { startDist, startZoom, startMid, startPan } */

    function originXY() {
        const r = area.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }

    function pointerArray() {
        return Array.from(pointers.values());
    }

    function startGesture() {
        const ps = pointerArray();
        if (ps.length !== 2) return;
        gesture = {
            startDist: dist(ps[0], ps[1]),
            startZoom: state.zoom,
            startMid:  midpoint(ps[0], ps[1]),
            startPan:  { x: state.panX, y: state.panY },
        };
    }

    function updateGesture() {
        if (!gesture) return;
        const ps = pointerArray();
        if (ps.length !== 2) return;

        const d = dist(ps[0], ps[1]);
        if (Math.abs(d - gesture.startDist) < PINCH_THRESHOLD_PX
            && Math.hypot(midpoint(ps[0], ps[1]).x - gesture.startMid.x,
                          midpoint(ps[0], ps[1]).y - gesture.startMid.y) < PINCH_THRESHOLD_PX) {
            /* Below noise floor — ignore micro-jitter. */
            return;
        }

        const newZoom = clamp(gesture.startZoom * (d / gesture.startDist), ZOOM_MIN, ZOOM_MAX);
        const O = originXY();

        /* Canvas-space coords of the start midpoint (computed under the
         * start transform). We pin this point to the current midpoint so
         * pinch-zoom + two-finger drag both feel anchored. */
        const focalCanvasX = (gesture.startMid.x - O.x - gesture.startPan.x) / gesture.startZoom;
        const focalCanvasY = (gesture.startMid.y - O.y - gesture.startPan.y) / gesture.startZoom;
        const mid = midpoint(ps[0], ps[1]);

        state.zoom = newZoom;
        state.panX = mid.x - O.x - newZoom * focalCanvasX;
        state.panY = mid.y - O.y - newZoom * focalCanvasY;

        onUpdate(state);
    }

    function endGesture() { gesture = null; }

    function onPointerDown(e) {
        if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
            try { area.setPointerCapture(e.pointerId); } catch {}
            startGesture();
            e.preventDefault();
        }
    }

    function onPointerMove(e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (gesture && pointers.size === 2) {
            updateGesture();
            e.preventDefault();
        }
    }

    function onPointerUp(e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.delete(e.pointerId);
        if (pointers.size < 2) endGesture();
    }

    /* Use 'pointerdown' on area to start tracking; move/up listen on the
     * window so a finger sliding outside the canvas area still terminates
     * cleanly. */
    area.addEventListener('pointerdown',   onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup',     onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    return function detach() {
        area.removeEventListener('pointerdown',   onPointerDown);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup',     onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
        pointers.clear();
        gesture = null;
    };
}
